import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
} from "reactstrap";
import axios from "../../../utills/api"
import TableContainer from "../../../Components/Common/TableContainer";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import SpaCategoryModal from "./CategoryModal/SpaCategoryModal";

const API_BASE = "/spa-services";
const CATEGORY_API = "/spa-settings";
const IMAGE_BASE_URL = axios.defaults.baseURL.replace("/api", ""); 

const ManageSpa = () => {
  document.title = "HMS | Manage Spa";
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState({
    category: "",
    type: "",
    name: "",
    description: "",
    price: "",
    publishedOn: "",
    imageUrl: "",
  });
const [categoryModal, setCategoryModal] =
  useState(false);
  const toggleModal = () => setModalOpen(!modalOpen);

  const token = localStorage.getItem("token");
  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
   
  // ✅ Fetch SPA categories
  const fetchCategories = async () => {
    try {
      const res = await axios.get(CATEGORY_API, axiosConfig);
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch SPA categories:", err);
      toast.error("Failed to fetch SPA categories");
    }
  };

  // ✅ Fetch SPA services
  const fetchServices = async () => {
    try {
      const res = await axios.get(API_BASE);
      const data = Array.isArray(res.data) ? res.data : [];
      const mappedData = data.map((item) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const pubDate = new Date(item.published_on).setHours(0, 0, 0, 0);
        return {
          ...item,
          publishedOn: item.published_on,
          imageUrl: item.image,
          status: pubDate <= today ? "Active" : "Inactive",
        };
      });
      setServices(mappedData);
    } catch (error) {
      console.error("Failed to fetch services:", error);
      toast.error("Failed to fetch services");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchServices();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Add or Update service
  const handleAddOrUpdateService = async () => {
    try {
      if (!form.name || !form.category || !form.price || !form.publishedOn || !form.type) {
        toast.error("Please fill all required fields");
        return;
      }

      // --- FormData (fixed) ---
      const formData = new FormData();
      formData.append("category", form.category);
      formData.append("type", form.type);
      formData.append("name", form.name);
      formData.append("description", form.description);
      formData.append("price", form.price);
      formData.append("published_on", form.publishedOn);

      // ✅ Append image only if user selected a file
      // ✅ File upload
if (form.imageUrl instanceof File) {
  formData.append("image", form.imageUrl);
}
// ✅ URL image
else if (
  typeof form.imageUrl === "string" &&
  form.imageUrl.trim() !== ""
) {
  formData.append("image", form.imageUrl);
}

      let res;
      if (editingService) {
        res = await axios.put(`${API_BASE}/${editingService._id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(API_BASE, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const savedData = res?.data || {};
      const serviceToAddOrUpdate = {
        category: savedData.category || form.category,
        type: savedData.type || form.type,
        name: savedData.name || form.name,
        description: savedData.description || form.description,
        price: savedData.price || form.price,
        publishedOn: savedData.published_on || form.publishedOn,
       imageUrl:
  savedData.image ||
  (typeof form.imageUrl === "string" ? form.imageUrl : ""),
        _id: savedData._id || editingService?._id || Math.random().toString(36).substr(2, 9),
        status: (() => {
          const today = new Date().setHours(0, 0, 0, 0);
          const pubDate = new Date(savedData.published_on || form.publishedOn).setHours(0, 0, 0, 0);
          return pubDate <= today ? "Active" : "Inactive";
        })(),
      };

      if (editingService) {
        setServices((prev) =>
          prev.map((s) => (s._id === editingService._id ? serviceToAddOrUpdate : s))
        );
        toast.success("Service updated successfully");
      } else {
        setServices((prev) => [...prev, serviceToAddOrUpdate]);
        toast.success("Service added successfully");
      }

      setForm({
        category: "",
        type: "",
        name: "",
        description: "",
        price: "",
        publishedOn: "",
        imageUrl: "",
      });
      setEditingService(null);
      toggleModal();
    } catch (error) {
      console.error("Failed to save service:", error.response?.data || error.message);
      toast.error("Failed to save service");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setForm({
      category: service.category || "",
      type: service.type || "",
      name: service.name || "",
      description: service.description || "",
      price: service.price || "",
      publishedOn: service.publishedOn
        ? new Date(service.publishedOn).toISOString().split("T")[0]
        : "",
      imageUrl: service.imageUrl || "",
    });
    toggleModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`);
      setServices((prev) => prev.filter((s) => s._id !== id));
      toast.success("Service deleted successfully");
    } catch (error) {
      console.error("Failed to delete service:", error);
      toast.error("Failed to delete service");
    }
  };

  const columnsSpaServices = useMemo(
    () => [
         {
        header: "Image",
        accessorKey: "image",
        cell: ({ row }) => (
          <img
            src={
              row.original.image?.startsWith("http")
                ? row.original.image
                : `${IMAGE_BASE_URL}${row.original.image}`
            }
            alt={row.original.name}
            width="50"
            height="50"
            className="rounded"
          />

        ),
      },
      { header: "Category", accessorKey: "category" },
      { header: "Type", accessorKey: "type" },
      { header: "Name", accessorKey: "name" },
      { header: "Price (₹)", accessorKey: "price" },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }) => {
          const [expanded, setExpanded] = React.useState(false);
          const text = row.original.description || "";
          const limit = 20;
          if (text.length <= limit) return <span>{text}</span>;
          return (
            <span>
              {expanded ? text : text.substring(0, limit) + "... "}
              <span
                onClick={() => setExpanded(!expanded)}
                style={{ color: "blue", cursor: "pointer" }}
              >
                {expanded ? "Read less" : "Read more"}
              </span>
            </span>
          );
        },
      },
      {
        header: "Published On",
        accessorKey: "publishedOn",
        cell: ({ cell }) =>
          cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ cell }) => (
          <span
            className={`badge ${cell.getValue() === "Active"
                ? "text-uppercase bg-success-subtle text-success"
                : "text-uppercase bg-danger-subtle text-danger"
              }`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <td className="d-flex justify-content-center align-items-center gap-2">
            <Link
              onClick={() => handleEdit(row.original)}
              className="btn btn-sm btn-soft-primary"
              style={{ width: "28px", height: "28px" }}
            >
              <i className="ri-edit-line"></i>
            </Link>

            <Link
              onClick={() => handleDelete(row.original._id)}
              className="btn btn-sm btn-soft-danger"
              style={{ width: "28px", height: "28px" }}
            >
              <i className="las la-trash-alt"></i>
            </Link>
          </td>
        ),
      },
    ],
    [services]
  );

  return (
    <>
    <Card>
     <CardHeader className="d-flex justify-content-between align-items-center">
  <h5 className="fs-16 mb-0">Manage Spa Services</h5>

  <div className="d-flex" style={{ gap: "10px" }}>
    <Row className="mb-0">
      <Col md={12}>
        <Input
          type="text"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Col>
    </Row>

    <Button
      color="success"
      onClick={() => setCategoryModal(true)}
    >
      + Spa Category
    </Button>

    <Button
      color="primary"
      onClick={() => {
        setEditingService(null);
        setForm({
          category: "",
          type: "",
          name: "",
          description: "",
          price: "",
          publishedOn: "",
          imageUrl: "",
        });
        toggleModal();
      }}
    >
      + Add Service
    </Button>
  </div>
</CardHeader>

      <CardBody>
        <div className="table-responsive" style={{ whiteSpace: "nowrap" }}>
          <TableContainer
            columns={columnsSpaServices}
            data={services}
            customPageSize={5}
            isPagination
            className="text-nowrap"
          />
        </div>
      </CardBody>

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {editingService ? "Edit Spa Service" : "Add Spa Service"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Category</Label>
              <Input
                type="select"
                name="category"
                value={form.category}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Type</Label>
              <Input name="type" value={form.type} onChange={handleChange} />
            </FormGroup>
            <FormGroup>
              <Label>Name</Label>
              <Input name="name" value={form.name} onChange={handleChange} />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                value={form.description}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Price (₹)</Label>
              <Input
                type="number"
                name="price"
                value={form.price}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Published On</Label>
              <Input
                type="date"
                name="publishedOn"
                value={form.publishedOn}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Image URL (optional)</Label>
              <Input
                name="imageUrl"
                value={form.imageUrl instanceof File ? "" : form.imageUrl}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Upload Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setForm({ ...form, imageUrl: e.target.files[0] })
                }
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddOrUpdateService}>
            {editingService ? "Update Service" : "Add Service"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Card>

  <SpaCategoryModal
  isOpen={categoryModal}
  toggle={() =>
    setCategoryModal(false)
  }
/>
</>
    
  );

};

export default ManageSpa;
